import React, { useMemo, useState } from 'react';
import {
  TrendingUp,
  CalendarDays,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Flame,
} from 'lucide-react';

const isThisWeek = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  return d >= startOfWeek && d <= endOfWeek;
};

const isOverdue = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

const isToday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  return d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
};

export default function BoardWidgets({ board }) {
  const [expanded, setExpanded] = useState(true);

  const stats = useMemo(() => {
    if (!board?.lists) return { total: 0, completed: 0, inProgress: 0, todo: 0, pct: 0, thisWeekCards: [] };

    let total = 0;
    let completed = 0;
    let inProgress = 0;
    const thisWeekCards = [];

    board.lists.forEach(list => {
      list.cards?.forEach(card => {
        total++;
        if (card.status === 'completed') completed++;
        else if (card.status === 'in_progress') inProgress++;

        if (card.due_date && isThisWeek(card.due_date) && card.status !== 'completed') {
          thisWeekCards.push({
            ...card,
            listName: list.name,
            overdue: isOverdue(card.due_date),
            dueToday: isToday(card.due_date),
          });
        }
      });
    });

    thisWeekCards.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, inProgress, todo: total - completed - inProgress, pct, thisWeekCards };
  }, [board]);

  if (!board?.lists?.length) return null;

  return (
    <div className="shrink-0" data-testid="board-widgets">
      {/* Toggle strip */}
      <button
        onClick={() => setExpanded(prev => !prev)}
        className="w-full flex items-center justify-center gap-1.5 py-1 bg-black/10 hover:bg-black/15 text-white/60 hover:text-white/80 transition-colors text-xs"
        data-testid="widgets-toggle-btn"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? 'Hide Insights' : 'Show Insights'}
      </button>

      {expanded && (
        <div className="px-4 py-3 flex gap-3 overflow-x-auto">
          {/* Journey Progress Widget */}
          <div
            className="flex-1 min-w-[280px] max-w-[420px] bg-white/12 backdrop-blur-md rounded-xl border border-white/15 p-4"
            data-testid="widget-journey-progress"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              </div>
              <h3 className="text-sm font-semibold text-white">Journey Progress</h3>
              <span className="ml-auto text-lg font-bold text-white">{stats.pct}%</span>
            </div>

            {/* Progress bar */}
            <div className="h-2.5 bg-white/10 rounded-full overflow-hidden mb-3">
              <div className="h-full flex">
                {stats.completed > 0 && (
                  <div
                    className="bg-emerald-500 transition-all duration-700 ease-out"
                    style={{ width: `${(stats.completed / stats.total) * 100}%` }}
                  />
                )}
                {stats.inProgress > 0 && (
                  <div
                    className="bg-amber-500 transition-all duration-700 ease-out"
                    style={{ width: `${(stats.inProgress / stats.total) * 100}%` }}
                  />
                )}
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                <span className="text-white/70">{stats.completed} Done</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-amber-400" />
                <span className="text-white/70">{stats.inProgress} Active</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full border border-white/30" />
                <span className="text-white/70">{stats.todo} Remaining</span>
              </div>
            </div>
          </div>

          {/* This Week Plan Widget */}
          <div
            className="flex-1 min-w-[280px] max-w-[420px] bg-white/12 backdrop-blur-md rounded-xl border border-white/15 p-4"
            data-testid="widget-this-week"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <CalendarDays className="w-4 h-4 text-blue-400" />
              </div>
              <h3 className="text-sm font-semibold text-white">This Week</h3>
              <span className="ml-auto text-xs text-white/50 font-medium">
                {stats.thisWeekCards.length} {stats.thisWeekCards.length === 1 ? 'task' : 'tasks'}
              </span>
            </div>

            {stats.thisWeekCards.length === 0 ? (
              <p className="text-xs text-white/40 italic py-2">No tasks due this week. You're ahead!</p>
            ) : (
              <div className="space-y-1.5 max-h-[100px] overflow-y-auto pr-1">
                {stats.thisWeekCards.slice(0, 5).map(card => (
                  <div
                    key={card.card_id}
                    className="flex items-center gap-2 py-1 px-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    {card.overdue ? (
                      <AlertCircle className="w-3 h-3 text-red-400 shrink-0" />
                    ) : card.dueToday ? (
                      <Flame className="w-3 h-3 text-orange-400 shrink-0" />
                    ) : (
                      <CalendarDays className="w-3 h-3 text-blue-300 shrink-0" />
                    )}
                    <span className="text-xs text-white/80 truncate flex-1">{card.title}</span>
                    <span className={`text-[10px] shrink-0 px-1.5 py-0.5 rounded ${
                      card.overdue
                        ? 'bg-red-500/20 text-red-300'
                        : card.dueToday
                        ? 'bg-orange-500/20 text-orange-300'
                        : 'bg-white/10 text-white/50'
                    }`}>
                      {card.dueToday
                        ? 'Today'
                        : new Date(card.due_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                ))}
                {stats.thisWeekCards.length > 5 && (
                  <p className="text-[10px] text-white/40 text-center py-1">
                    +{stats.thisWeekCards.length - 5} more
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
